import { useRef, useEffect, useState, useCallback } from 'react';
import { cn } from '@/utils/cn';
import type { Annotation } from './RegionAnnotator';

interface AnnotationTimelineProps {
  totalFrames: number;
  fps: number;
  currentFrame: number;
  annotations: Annotation[];
  onSeek: (frame: number) => void;
  className?: string;
}

interface HoverInfo {
  x: number;
  y: number;
  frame: number;
  annotation?: Annotation;
}

/**
 * AnnotationTimeline - Canvas-based timeline visualization for video annotations
 *
 * Features:
 * - Horizontal timeline showing full video duration
 * - Frame markers with time labels
 * - Visual annotation markers (dots for single frames, bars for ranges)
 * - Playhead indicator showing current position
 * - Click to seek functionality
 * - Hover tooltips for frames and annotations
 */
function AnnotationTimeline({
  totalFrames,
  fps,
  currentFrame,
  annotations,
  onSeek,
  className,
}: AnnotationTimelineProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const [canvasWidth, setCanvasWidth] = useState(800);

  // Constants for timeline layout
  const TIMELINE_HEIGHT = 60;
  const PADDING = 20;
  // Use actual fps for markers - shows 1 marker per second regardless of frame rate
  const FRAME_MARKER_INTERVAL = Math.max(1, Math.round(fps));

  // Update canvas width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setCanvasWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Helper: Convert frame to X position
  const frameToX = useCallback(
    (frame: number): number => {
      const percent = frame / Math.max(totalFrames - 1, 1);
      return PADDING + percent * (canvasWidth - PADDING * 2);
    },
    [totalFrames, canvasWidth]
  );

  // Helper: Convert X position to frame
  const xToFrame = useCallback(
    (x: number): number => {
      const percent = (x - PADDING) / (canvasWidth - PADDING * 2);
      return Math.floor(percent * (totalFrames - 1));
    },
    [totalFrames, canvasWidth]
  );

  // Helper: Format time as seconds
  const formatTime = useCallback(
    (frame: number): string => {
      const seconds = frame / fps;
      return `${seconds.toFixed(1)}s`;
    },
    [fps]
  );

  // Draw timeline on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvasWidth;
    canvas.height = TIMELINE_HEIGHT;

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, TIMELINE_HEIGHT);

    // Draw background bar
    const barY = TIMELINE_HEIGHT / 2 - 2;
    ctx.fillStyle = '#e5e7eb'; // gray-200
    ctx.fillRect(PADDING, barY, canvasWidth - PADDING * 2, 4);

    // Draw frame markers and time labels
    const markerInterval = FRAME_MARKER_INTERVAL;
    for (let frame = 0; frame <= totalFrames - 1; frame += markerInterval) {
      const x = frameToX(frame);

      // Draw tick mark
      ctx.fillStyle = '#9ca3af'; // gray-400
      ctx.fillRect(x - 0.5, barY - 6, 1, 16);

      // Draw time label
      ctx.fillStyle = '#6b7280'; // gray-500
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(formatTime(frame), x, TIMELINE_HEIGHT - 5);
    }

    // Draw annotation markers
    annotations.forEach((ann) => {
      const color = ann.color || '#3b82f6';

      if (ann.activeFrames?.mode === 'range' &&
          ann.activeFrames.startFrame !== undefined &&
          ann.activeFrames.endFrame !== undefined) {
        // Draw as horizontal bar for range annotations
        const startX = frameToX(ann.activeFrames.startFrame);
        const endX = frameToX(ann.activeFrames.endFrame);
        const barWidth = Math.max(endX - startX, 2); // Minimum 2px width

        // Fill with semi-transparent color
        ctx.fillStyle = color + '80'; // 50% opacity
        ctx.fillRect(startX, barY - 8, barWidth, 20);

        // Border
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.strokeRect(startX, barY - 8, barWidth, 20);
      } else {
        // Draw as dot for single-frame or "all frames" annotations
        const frame = ann.frame || 0;
        const x = frameToX(frame);

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, TIMELINE_HEIGHT / 2, 5, 0, Math.PI * 2);
        ctx.fill();

        // White border for visibility
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    });

    // Draw playhead indicator
    const playheadX = frameToX(currentFrame);

    // Vertical line
    ctx.strokeStyle = '#ef4444'; // red-500
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(playheadX, 10);
    ctx.lineTo(playheadX, TIMELINE_HEIGHT - 15);
    ctx.stroke();

    // Triangle at top
    ctx.fillStyle = '#ef4444';
    ctx.beginPath();
    ctx.moveTo(playheadX, 10);
    ctx.lineTo(playheadX - 5, 2);
    ctx.lineTo(playheadX + 5, 2);
    ctx.closePath();
    ctx.fill();

  }, [totalFrames, fps, currentFrame, annotations, canvasWidth, frameToX, formatTime, FRAME_MARKER_INTERVAL]);

  // Handle click to seek
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;

      const targetFrame = xToFrame(x);
      const clampedFrame = Math.max(0, Math.min(totalFrames - 1, targetFrame));

      onSeek(clampedFrame);
    },
    [xToFrame, totalFrames, onSeek]
  );

  // Handle mouse move for tooltip
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const frame = xToFrame(x);
      const clampedFrame = Math.max(0, Math.min(totalFrames - 1, frame));

      // Check if hovering over an annotation
      let hoveredAnnotation: Annotation | undefined;

      for (const ann of annotations) {
        if (ann.activeFrames?.mode === 'range' &&
            ann.activeFrames.startFrame !== undefined &&
            ann.activeFrames.endFrame !== undefined) {
          // Check if hovering over range bar
          const startX = frameToX(ann.activeFrames.startFrame);
          const endX = frameToX(ann.activeFrames.endFrame);
          const barY = TIMELINE_HEIGHT / 2 - 8;

          if (x >= startX && x <= endX && y >= barY && y <= barY + 20) {
            hoveredAnnotation = ann;
            break;
          }
        } else {
          // Check if hovering over dot
          const annFrame = ann.frame || 0;
          const annX = frameToX(annFrame);
          const distance = Math.sqrt(
            Math.pow(x - annX, 2) + Math.pow(y - TIMELINE_HEIGHT / 2, 2)
          );

          if (distance <= 8) {
            hoveredAnnotation = ann;
            break;
          }
        }
      }

      setHoverInfo({
        x: e.clientX,
        y: e.clientY,
        frame: clampedFrame,
        annotation: hoveredAnnotation,
      });
    },
    [annotations, xToFrame, totalFrames, frameToX]
  );

  // Clear hover info on mouse leave
  const handleMouseLeave = useCallback(() => {
    setHoverInfo(null);
  }, []);

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {/* Timeline canvas */}
      <div className="relative w-full bg-dark-elevated-1 rounded-lg border border-border-subtle overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-pointer"
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
      </div>

      {/* Hover tooltip */}
      {hoverInfo && (
        <div
          className="fixed bg-black text-white text-xs px-2 py-1 rounded pointer-events-none z-50 shadow-lg"
          style={{
            left: hoverInfo.x + 10,
            top: hoverInfo.y - 35,
          }}
        >
          {hoverInfo.annotation ? (
            <div className="space-y-0.5">
              <div className="font-semibold">{hoverInfo.annotation.description || 'Unnamed'}</div>
              {hoverInfo.annotation.activeFrames?.mode === 'range' &&
               hoverInfo.annotation.activeFrames.startFrame !== undefined &&
               hoverInfo.annotation.activeFrames.endFrame !== undefined ? (
                <div className="text-gray-300">
                  Frames {hoverInfo.annotation.activeFrames.startFrame} - {hoverInfo.annotation.activeFrames.endFrame}
                  {' '}({formatTime(hoverInfo.annotation.activeFrames.startFrame)} - {formatTime(hoverInfo.annotation.activeFrames.endFrame)})
                </div>
              ) : (
                <div className="text-gray-300">
                  Frame {hoverInfo.annotation.frame ?? 0} ({formatTime(hoverInfo.annotation.frame ?? 0)})
                </div>
              )}
            </div>
          ) : (
            <div>
              Frame {hoverInfo.frame} ({formatTime(hoverInfo.frame)})
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AnnotationTimeline;
